import { MeiucaService as BaseMeiucaService } from '@meiuca/service'
import { env } from '@/lib/env'
import type { StageView } from '../types/tournament'

/**
 * Public follow-along payload for a tournament event. Mirrors the Swift
 * `PublicEventResponse` — presentation fields only, no owner ids or management
 * data. `stage` is null for a category with no bracket saved yet.
 */
export interface PublicEventPayload {
  name: string
  /** Rich description as markdown; rendered safely (no raw HTML). */
  description: string | null
  location: string | null
  bannerImageUrl: string | null
  ownerName: string
  startsOn: string | null
  endsOn: string | null
  categories: PublicEventCategory[]
}

export interface PublicEventCategory {
  id: string
  name: string
  /** Raw backend status: draft | inProgress | completed | ... */
  status: string
  championTeamId: string | null
  /** The champion's name, for the overview list (null until decided). */
  championName: string | null
  stage: StageView | null
}

/**
 * Reads the public follow-along payload of a tournament event by its opaque
 * token. The route is on the app's own API (`meiuca_app/v1`) and does not
 * require login — the token is the credential and the event's visibility
 * governs access (a private or unknown event returns `NotFoundError`).
 */
class TournamentPublicService extends BaseMeiucaService {
  async getEvent(token: string): Promise<PublicEventPayload> {
    const response = await this.get<{ data: PublicEventPayload }>(
      `/tournament-events/${encodeURIComponent(token)}`
    )
    // Backend wraps every payload in MeiucaResponse { data: ... }.
    return response.data.data
  }
}

export const tournamentPublicService = new TournamentPublicService({
  // The app's normal API surface (`{host}/meiuca_app/v1`), same base the rest
  // of the site uses. The route opens without a session; the token governs.
  baseURL: env.API_URL,
  refreshEndpoint: '/v1/auth/refresh',
  cookiePrefix: '',
  debug: env.IS_DEVELOPMENT
})
